import { useState } from "react";
import type { CustomColumn } from "../types/types";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

// コンポーネント外に定義しないと、親 state 変化のたびに再マウントされてフォーカスが外れる
const OptionsEditor = ({
  options,
  isEdit,
  inputClass,
  accentColor,
  onUpdate,
  onAdd,
  onRemove,
}: {
  options: string[];
  isEdit: boolean;
  inputClass: string;
  accentColor: string;
  onUpdate: (index: number, value: string, isEdit: boolean) => void;
  onAdd: (isEdit: boolean) => void;
  onRemove: (index: number, isEdit: boolean) => void;
}) => (
  <div className="mt-2 space-y-1">
    {options.map((opt, i) => (
      <div key={i} className="flex items-center gap-1">
        <input
          type="text"
          value={opt}
          onChange={(e) => onUpdate(i, e.target.value, isEdit)}
          placeholder={`選択肢 ${i + 1}`}
          className={inputClass}
        />
        {options.length > 1 && (
          <button
            onClick={() => onRemove(i, isEdit)}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        )}
      </div>
    ))}
    <button
      onClick={() => onAdd(isEdit)}
      className="flex items-center gap-1 text-xs mt-1 px-2 py-1 rounded hover:bg-gray-100"
      style={{ color: accentColor }}
    >
      <AddIcon style={{ fontSize: 14 }} />
      選択肢を追加
    </button>
  </div>
);

interface Props {
  columns: CustomColumn[];
  listId: string;
  honbuId: number;
  color: PaletteColor;
  isDarkMode: boolean;
  onClose: () => void;
  onColumnsChange: (columns: CustomColumn[]) => void;
}

interface NewColumnForm {
  columnName: string;
  columnType: "text" | "select";
  options: string[];
}

const emptyForm = (): NewColumnForm => ({
  columnName: "",
  columnType: "text",
  options: [""],
});

export const DashboardCustomColumnDialog = ({
  columns,
  listId,
  honbuId,
  color,
  isDarkMode,
  onClose,
  onColumnsChange,
}: Props) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState<NewColumnForm>(emptyForm());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<NewColumnForm>(emptyForm());
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accentColor = PaletteColorHex[color];

  const headerStyle = {
    background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
  };

  // ダークモードでは文字が沈みやすいため白寄りにする（DashboardView と合わせる）
  const bgStyle = isDarkMode
    ? { backgroundColor: "#1e1e1e", color: "#f5f5f5" }
    : { backgroundColor: "#ffffff", color: "#212121" };

  // 列追加
  const handleAdd = async () => {
    if (!form.columnName.trim()) {
      setError("列名を入力してください");
      return;
    }
    if (form.columnType === "select" && form.options.filter((o) => o.trim()).length === 0) {
      setError("選択肢を1つ以上入力してください");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        columnName: form.columnName.trim(),
        columnType: form.columnType,
        options:
          form.columnType === "select"
            ? form.options.filter((o) => o.trim()).map((o) => o.trim())
            : null,
      };
      const res = await fetch(
        `/api/todolists/${listId}/custom-columns?honbuId=${honbuId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      if (!res.ok) throw new Error("追加に失敗しました");
      const created: CustomColumn = await res.json();
      onColumnsChange([...columns, created]);
      setForm(emptyForm());
      setShowAddForm(false);
    } catch {
      setError("保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // 列更新
  const handleUpdate = async (columnId: number) => {
    if (!editForm.columnName.trim()) {
      setError("列名を入力してください");
      return;
    }
    if (
      editForm.columnType === "select" &&
      editForm.options.filter((o) => o.trim()).length === 0
    ) {
      setError("選択肢を1つ以上入力してください");
      return;
    }
    setSaving(true);
    setError(null);
    const col = columns.find((c) => c.customColumnId === columnId);
    try {
      const body = {
        columnName: editForm.columnName.trim(),
        columnType: editForm.columnType,
        displayOrder: col?.displayOrder ?? 0,
        options:
          editForm.columnType === "select"
            ? editForm.options.filter((o) => o.trim()).map((o) => o.trim())
            : null,
      };
      const res = await fetch(`/api/custom-columns/${columnId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      onColumnsChange(
        columns.map((c) =>
          c.customColumnId === columnId
            ? {
                ...c,
                columnName: editForm.columnName.trim(),
                columnType: editForm.columnType,
                options:
                  editForm.columnType === "select"
                    ? editForm.options
                        .filter((o) => o.trim())
                        .map((o, i) => ({ optionId: 0, optionValue: o.trim(), displayOrder: i }))
                    : [],
              }
            : c
        )
      );
      setEditingId(null);
    } catch {
      setError("更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  // 列削除
  const handleDelete = async (columnId: number) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/custom-columns/${columnId}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      onColumnsChange(columns.filter((c) => c.customColumnId !== columnId));
      setDeletingId(null);
    } catch {
      setError("削除に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (col: CustomColumn) => {
    setEditingId(col.customColumnId);
    setEditForm({
      columnName: col.columnName,
      columnType: col.columnType,
      options: col.options.length > 0 ? col.options.map((o) => o.optionValue) : [""],
    });
    setError(null);
  };

  const addOption = (isEdit: boolean) => {
    if (isEdit) {
      setEditForm((f) => ({ ...f, options: [...f.options, ""] }));
    } else {
      setForm((f) => ({ ...f, options: [...f.options, ""] }));
    }
  };

  const removeOption = (index: number, isEdit: boolean) => {
    if (isEdit) {
      setEditForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }));
    } else {
      setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }));
    }
  };

  const updateOption = (index: number, value: string, isEdit: boolean) => {
    if (isEdit) {
      setEditForm((f) => {
        const opts = [...f.options];
        opts[index] = value;
        return { ...f, options: opts };
      });
    } else {
      setForm((f) => {
        const opts = [...f.options];
        opts[index] = value;
        return { ...f, options: opts };
      });
    }
  };

  const inputClass = `w-full px-3 py-1.5 rounded border text-sm ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400"
      : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
  } focus:outline-none`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        className="relative rounded-lg shadow-xl w-full max-w-md mx-4 flex flex-col overflow-hidden"
        style={{
          ...bgStyle,
          border: isDarkMode ? "1px solid #000" : "1px solid #fff",
          maxHeight: "80vh",
        }}
      >
        {/* ヘッダー */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={headerStyle}
        >
          <span className="font-semibold text-white text-sm">カスタム列の管理</span>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* 本文 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="text-red-500 text-xs px-2 py-1 rounded bg-red-50">
              {error}
            </div>
          )}

          {/* 既存列一覧 */}
          {columns.length === 0 && !showAddForm && (
            <p className="text-sm text-gray-400 text-center py-4">
              カスタム列がまだありません
            </p>
          )}

          {columns.map((col) => (
            <div
              key={col.customColumnId}
              className={`rounded-lg border px-3 py-2 ${
                isDarkMode ? "border-gray-500" : "border-gray-200"
              }`}
            >
              {editingId === col.customColumnId ? (
                // 編集フォーム
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editForm.columnName}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, columnName: e.target.value }))
                    }
                    placeholder="列名"
                    className={inputClass}
                  />
                  <div className="flex gap-3 text-sm">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        checked={editForm.columnType === "text"}
                        onChange={() =>
                          setEditForm((f) => ({ ...f, columnType: "text" }))
                        }
                      />
                      テキスト
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="radio"
                        checked={editForm.columnType === "select"}
                        onChange={() => {
                          setEditForm((f) => ({
                            ...f,
                            columnType: "select",
                            options: f.options.length === 0 ? [""] : f.options,
                          }));
                        }}
                      />
                      選択肢
                    </label>
                  </div>
                  {editForm.columnType === "select" && (
                    <OptionsEditor
                      options={editForm.options}
                      isEdit={true}
                      inputClass={inputClass}
                      accentColor={accentColor}
                      onUpdate={updateOption}
                      onAdd={addOption}
                      onRemove={removeOption}
                    />
                  )}
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => setEditingId(null)}
                      className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => handleUpdate(col.customColumnId)}
                      disabled={saving}
                      className="px-3 py-1 text-sm rounded text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      保存
                    </button>
                  </div>
                </div>
              ) : deletingId === col.customColumnId ? (
                // 削除確認
                <div className="space-y-2">
                  <p className="text-sm text-red-500">
                    「{col.columnName}」を削除しますか？
                    <br />
                    <span className="text-xs">この列のすべての入力データも削除されます。</span>
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setDeletingId(null)}
                      className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50"
                    >
                      キャンセル
                    </button>
                    <button
                      onClick={() => handleDelete(col.customColumnId)}
                      disabled={saving}
                      className="px-3 py-1 text-sm rounded text-white bg-red-500 hover:bg-red-600"
                    >
                      削除する
                    </button>
                  </div>
                </div>
              ) : (
                // 表示
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium">{col.columnName}</span>
                    <span className="ml-2 text-xs text-gray-400">
                      {col.columnType === "text" ? "テキスト" : "選択肢"}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => startEdit(col)}
                      className="px-2 py-0.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => setDeletingId(col.customColumnId)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <DeleteOutlineIcon style={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* 追加フォーム */}
          {showAddForm && (
            <div
              className={`rounded-lg border px-3 py-3 space-y-2 ${
                isDarkMode ? "border-gray-500" : "border-blue-200 bg-blue-50/30"
              }`}
            >
              <p className="text-xs font-semibold" style={{ color: accentColor }}>
                新しい列
              </p>
              <input
                type="text"
                value={form.columnName}
                onChange={(e) => setForm((f) => ({ ...f, columnName: e.target.value }))}
                placeholder="列名"
                className={inputClass}
                autoFocus
              />
              <div className="flex gap-3 text-sm">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    checked={form.columnType === "text"}
                    onChange={() => setForm((f) => ({ ...f, columnType: "text" }))}
                  />
                  テキスト
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    checked={form.columnType === "select"}
                    onChange={() =>
                      setForm((f) => ({
                        ...f,
                        columnType: "select",
                        options: [""],
                      }))
                    }
                  />
                  選択肢
                </label>
              </div>
              {form.columnType === "select" && (
                <OptionsEditor
                  options={form.options}
                  isEdit={false}
                  inputClass={inputClass}
                  accentColor={accentColor}
                  onUpdate={updateOption}
                  onAdd={addOption}
                  onRemove={removeOption}
                />
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setForm(emptyForm());
                    setError(null);
                  }}
                  className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  onClick={handleAdd}
                  disabled={saving}
                  className="px-3 py-1 text-sm rounded text-white"
                  style={{ backgroundColor: accentColor }}
                >
                  追加する
                </button>
              </div>
            </div>
          )}

          {/* 追加ボタン */}
          {!showAddForm && (
            <button
              onClick={() => {
                setShowAddForm(true);
                setError(null);
              }}
              className="w-full py-2 text-sm rounded-lg border-2 border-dashed flex items-center justify-center gap-1 transition-colors hover:opacity-80"
              style={{
                borderColor: accentColor,
                color: accentColor,
              }}
            >
              <AddIcon style={{ fontSize: 16 }} />
              新しい列を追加
            </button>
          )}
        </div>

        {/* フッター */}
        <div
          className={`px-4 py-3 flex justify-end border-t ${
            isDarkMode ? "border-gray-500" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
